import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@material-ui/core';
import { UseMutationOptions } from 'react-query';

import { DataTable, DataTableActions, DataTableColumn, DataTableRowSelection } from 'components';
import { InfiniteQuery, UseCheckedState } from 'types/app';
import { useProtectedClient } from 'hooks';
import {
  AssignmentFragment,
  DeleteAssignmentMutation,
  DeleteAssignmentMutationVariables,
} from 'api';
import { generateLink } from 'Links';

import { Actions } from './index';

type DeleteMutationOptions = UseMutationOptions<
  DeleteAssignmentMutation,
  unknown,
  DeleteAssignmentMutationVariables
>;

interface Props extends Partial<UseCheckedState>, InfiniteQuery<AssignmentFragment> {
  count: number;
  isActive: boolean;
  deleteMutationOptions: DeleteMutationOptions;
}

const Table: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  count,
  isActive,
  deleteMutationOptions,
  ...rest
}) => {
  const { t } = useTranslation();
  const client = useProtectedClient();

  const { setCheckedState, checkedMode, resetCheckedState } = rest;

  const columns: DataTableColumn<AssignmentFragment>[] = [
    {
      width: 300,
      headerCell: {
        render: t('common:title'),
      },
      bodyCell: {
        render: assignment => (
          <Link component={generateLink(`/admin/settings/assignments/${assignment.id}`)}>
            <Typography noWrap variant="subtitle2">
              {assignment.title}
            </Typography>
          </Link>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('admin:requiredGroups'),
      },
      bodyCell: {
        render: assignment => <Typography noWrap>{assignment.groups_required}</Typography>,
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('admin:suggestedGroups'),
      },
      bodyCell: {
        render: assignment => <Typography noWrap>{assignment.groups_suggested}</Typography>,
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('admin:requiredUsers'),
      },
      bodyCell: {
        render: assignment => <Typography noWrap>{assignment.users_required}</Typography>,
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('admin:suggestedUsers'),
      },
      bodyCell: {
        render: assignment => <Typography noWrap>{assignment.users_suggested}</Typography>,
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<AssignmentFragment> = assignment => {
    return (
      <Actions
        count={1}
        isActive={isActive}
        variables={{ id: assignment.id, client_id: client.id }}
        deleteMutationOptions={deleteMutationOptions}
        resetCheckedState={resetCheckedState}
        title={assignment.title}
      />
    );
  };

  return (
    <DataTable
      data={results}
      columns={columns}
      actions={actions}
      pagination={pagination}
      rowSelection={
        <DataTableRowSelection
          noun={t('common:users').toLowerCase()}
          count={results.length}
          total={count}
          mode={checkedMode}
          onSelectAll={setCheckedState}
          disableMatchAll
        />
      }
    />
  );
};

export default Table;
